// externals
import React, { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';

// components
import { Alert } from '@interstate/components/Alert';
import { Box } from '@interstate/components/Box';
import { Button } from '@interstate/components/Button';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { Typography } from '@interstate/components/Typography';

// libraries
import {
    FirstPencilCreatingOverlay,
    FirstPencilSciRequestAlert,
    ManualTemplateList,
    SlideOutWithFixedFooter,
    useGetDrawerWidth
} from '@makemydeal/dr-dash-components';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as firstPencilActionCreators from '@makemydeal/dr-dash-store/actions/firstPencilActionCreators';
import * as firstPencilSelectors from '@makemydeal/dr-dash-store/selectors/firstPencilSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { logFirstPencilCreateClicked } from '@makemydeal/dr-dash-store/utils/firstPencilTelemetry';
import type { DealTemplate } from '@makemydeal/dr-dash-types';
import type { FirstPencilSetupMode } from '@makemydeal/dr-dash-store/types/firstPencilTypes';

// components (local)
import { LabelledValue } from './LabelledValue';

// consts/enums
import { CREATE_DEAL_IDENTIFIER, NO_VEHICLE_SELECTED, VEHICLE_OF_INTEREST_LABEL } from './createDealConsts';

// styles
import {
    SetupModeCard,
    SetupModeCardGroup,
    SetupModeDescription,
    SetupModeRadio,
    StockNumberActions,
    StockNumberField,
    StockNumberRow,
    TemplateSection
} from './CreateDealModal.style';

const APPLY_ERRORS: Record<FirstPencilSetupMode, { title: string; message: string }> = {
    template: {
        title: 'Template not applied',
        message: 'Something went wrong, template is not applied. Please try again.'
    },
    programs: {
        title: 'Programs & incentives not applied',
        message: 'Something went wrong, programs & incentives are not applied. Please try again.'
    }
};

// Condition arrives in any casing off the BFF; getVehicleConditionLower normalises it first.
const capitalize = (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

const ARROW_OFFSETS: Record<string, number | undefined> = {
    ArrowRight: 1,
    ArrowDown: 1,
    ArrowLeft: -1,
    ArrowUp: -1
};

/** Before anything is chosen the first tile holds the tab stop; after, the checked one does. */
const isFocusableTile = (mode: FirstPencilSetupMode, setupMode: FirstPencilSetupMode | undefined, index: number) =>
    setupMode ? setupMode === mode : index === 0;

const SETUP_MODES: { mode: FirstPencilSetupMode; label: string; description: string }[] = [
    { mode: 'template', label: 'Template', description: 'Use a saved deal template to auto-populate scenarios' },
    {
        mode: 'programs',
        label: 'Programs & Incentives',
        description: 'Select programs and deal types to build custom scenarios'
    }
];

export const CreateDealSlideOutContent: React.FC = () => {
    const dispatch = useDispatch();
    // Interstate's theme context is separate from styled-components'; bridge it.
    const theme = useInterstateTheme();
    const panelWidth = useGetDrawerWidth();

    const shopper = useSelector(offerReduxSelectors.getShopperInfo);
    const hasVehicle = useSelector(vehicleSelectors.getHasVehicle);
    const vehicleYear = useSelector(vehicleSelectors.getVehicleYear);
    const vehicleMake = useSelector(vehicleSelectors.getVehicleMake);
    const vehicleModel = useSelector(vehicleSelectors.getVehicleModel);
    const vehicleTrim = useSelector(vehicleSelectors.getVehicleTrim);
    const vehicleCondition = useSelector(vehicleSelectors.getVehicleConditionLower);
    const vehicleStockNumber = useSelector(vehicleSelectors.getVehicleStockNumber);
    const setupMode = useSelector(firstPencilSelectors.getSelectedSetupMode);
    const selectedTemplateId = useSelector(firstPencilSelectors.getSelectedTemplateId);
    const selectedProgram = useSelector(firstPencilSelectors.getSelectedProgram);
    const selectedVehicle = useSelector(firstPencilSelectors.getSelectedVehicle);
    const canConfirm = useSelector(firstPencilSelectors.canConfirmCreate);
    const isApplying = useSelector(firstPencilSelectors.isApplyingSelection);
    const hasApplyError = useSelector(firstPencilSelectors.hasApplyError);

    /** This component only mounts while the modal is shown, so its mount time is the modal's open time. */
    const openedAtRef = useRef(Date.now());
    /** Single-select today; derived rather than hardcoded so a future multi-select (F314089) needs no change here. */
    const scenarioCount =
        setupMode === 'template' ? (selectedTemplateId ? 1 : 0) : setupMode === 'programs' ? (selectedProgram ? 1 : 0) : 0;

    const customerName = useMemo(
        () => [shopper?.firstName, shopper?.lastName].filter(Boolean).join(' '),
        [shopper?.firstName, shopper?.lastName]
    );

    const vehicleOfInterest = useMemo(() => {
        // A Search Inventory pick takes precedence over the deal's own vehicle.
        if (selectedVehicle) {
            return [
                capitalize((selectedVehicle.condition || selectedVehicle.type || '').toLowerCase()),
                selectedVehicle.year || '',
                selectedVehicle.make,
                selectedVehicle.model,
                selectedVehicle.trim
            ]
                .filter(Boolean)
                .join(' ');
        }
        if (!hasVehicle) {
            return '';
        }
        return [capitalize(vehicleCondition), vehicleYear || '', vehicleMake, vehicleModel, vehicleTrim].filter(Boolean).join(' ');
    }, [selectedVehicle, hasVehicle, vehicleCondition, vehicleYear, vehicleMake, vehicleModel, vehicleTrim]);

    const stockNumber = (selectedVehicle ? selectedVehicle.stockNumber : vehicleStockNumber) || '';

    // getHasVehicle is make || model || trim, so a stock-number-only CRM lead reads as vehicle-less.
    // A stock number identifies a vehicle of interest, so it enables the paths too. A pick counts
    // whatever it carries — a built vehicle has neither a stock number nor anything on the deal.
    const hasVehicleOfInterest = Boolean(selectedVehicle) || hasVehicle || Boolean(stockNumber);

    const handleSetupModeSelect = useCallback(
        (mode: FirstPencilSetupMode, openScreen = true) => {
            if (!hasVehicleOfInterest) {
                return;
            }
            dispatch(firstPencilActionCreators.setupModeSelected(mode));
            if (openScreen && mode === 'programs') {
                dispatch(baseDealActionCreators.openSlideOut(baseDealActionCreators.SlideOutType.FirstPencilIncentives));
            }
        },
        [dispatch, hasVehicleOfInterest]
    );

    /**
     * Arrow keys move and select within the group, Enter/Space select the focused tile — the semantics
     * a `role="radiogroup"` implies and screen readers send.
     */
    const handleTileKeyDown = useCallback(
        (evt: React.KeyboardEvent, index: number) => {
            if (!hasVehicleOfInterest) {
                return;
            }
            if (evt.key === 'Enter' || evt.key === ' ') {
                evt.preventDefault();
                handleSetupModeSelect(SETUP_MODES[index].mode);
                return;
            }
            const offset = ARROW_OFFSETS[evt.key];
            if (!offset) {
                return;
            }
            evt.preventDefault();
            const next = (index + offset + SETUP_MODES.length) % SETUP_MODES.length;
            handleSetupModeSelect(SETUP_MODES[next].mode, false);
            (evt.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
        },
        [hasVehicleOfInterest, handleSetupModeSelect]
    );

    // Opens FirstPencilChangeVehicleSlideOut, which layers on top without unmounting this one.
    const handleSearchInventory = useCallback(() => {
        dispatch(baseDealActionCreators.openSlideOut(baseDealActionCreators.SlideOutType.FirstPencilChangeVehicle));
    }, [dispatch]);

    const handleTemplateSelect = useCallback(
        (template: DealTemplate) => {
            dispatch(firstPencilActionCreators.templateSelected(template.id, template.name));
        },
        [dispatch]
    );

    const handleCancel = useCallback(() => {
        dispatch(firstPencilActionCreators.cancelled());
    }, [dispatch]);

    /**
     * Only Cancel and the header X dismiss. SlideOut forwards `onHide` to a temporary MUI Drawer as
     * `onClose`, which also fires for backdrop clicks and Escape — and a dismissed intercept cannot
     * reopen without a page reload, because the middleware's session guard is already primed. The X
     * arrives here as a click handler with no `reason`, so the two are distinguishable.
     */
    const handleHide = useCallback(
        (_event?: unknown, reason?: string) => {
            if (isApplying || reason === 'backdropClick' || reason === 'escapeKeyDown') {
                return;
            }
            handleCancel();
        },
        [handleCancel, isApplying]
    );

    const handleNext = useCallback(() => {
        logFirstPencilCreateClicked({
            setupMode,
            elapsedMs: Date.now() - openedAtRef.current,
            scenarioCount
        });
        dispatch(firstPencilActionCreators.createConfirmed());
    }, [dispatch, setupMode, scenarioCount]);

    return (
        <ThemeProvider theme={theme}>
            <SlideOutWithFixedFooter
                position="right"
                panelWidth={panelWidth}
                id={CREATE_DEAL_IDENTIFIER}
                data-testid={CREATE_DEAL_IDENTIFIER}
                show
                header={<Typography variant="h4">Create Deal</Typography>}
                onHide={handleHide}
                footer={{
                    layout: 'rightAligned',
                    options: {
                        action: handleCancel,
                        label: 'Cancel',
                        buttonStyle: 'tertiary',
                        disabled: isApplying,
                        'data-testid': `${CREATE_DEAL_IDENTIFIER}-cancel`
                    },
                    primary: {
                        action: handleNext,
                        label: 'Next',
                        disabled: !canConfirm || isApplying,
                        'data-testid': `${CREATE_DEAL_IDENTIFIER}-next`
                    }
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <FirstPencilSciRequestAlert screen="create-deal" />

                    {hasApplyError && setupMode && (
                        <Alert
                            type="error"
                            title={APPLY_ERRORS[setupMode].title}
                            data-testid={`${CREATE_DEAL_IDENTIFIER}-apply-error`}
                        >
                            {APPLY_ERRORS[setupMode].message}
                        </Alert>
                    )}

                    <LabelledValue label="Customer Name" value={customerName} name="customer-name" />

                    <StockNumberRow>
                        <StockNumberField>
                            <LabelledValue label="Stock Number" value={stockNumber} name="stock-number" />
                        </StockNumberField>
                        <StockNumberActions>
                            <Typography variant="strong-sm">OR</Typography>
                            <Button
                                buttonStyle="primary"
                                data-testid={`${CREATE_DEAL_IDENTIFIER}-search-inventory`}
                                onClick={handleSearchInventory}
                            >
                                Search Inventory
                            </Button>
                        </StockNumberActions>
                    </StockNumberRow>

                    <LabelledValue
                        label={VEHICLE_OF_INTEREST_LABEL}
                        value={vehicleOfInterest}
                        name="vehicle-of-interest"
                        placeholder={NO_VEHICLE_SELECTED}
                    />

                    <SetupModeCardGroup role="radiogroup" aria-label="Deal setup method">
                        {SETUP_MODES.map(({ mode, label, description }, index) => {
                            const selected = setupMode === mode;
                            return (
                                <SetupModeCard
                                    key={mode}
                                    role="radio"
                                    // Roving tabindex: a radiogroup is one tab stop, moved between with arrows.
                                    tabIndex={hasVehicleOfInterest && isFocusableTile(mode, setupMode, index) ? 0 : -1}
                                    aria-checked={selected}
                                    aria-disabled={!hasVehicleOfInterest}
                                    $selected={selected}
                                    $disabled={!hasVehicleOfInterest}
                                    data-testid={`${CREATE_DEAL_IDENTIFIER}-mode-${mode}`}
                                    onClick={() => handleSetupModeSelect(mode)}
                                    onKeyDown={(evt) => handleTileKeyDown(evt, index)}
                                >
                                    <SetupModeRadio $selected={selected} aria-hidden="true" />
                                    <Typography variant="strong-md" tag="span">
                                        {label}
                                    </Typography>
                                    <SetupModeDescription>
                                        <Typography variant="body-sm" color="base.color.gray.600" tag="span">
                                            {description}
                                        </Typography>
                                    </SetupModeDescription>
                                </SetupModeCard>
                            );
                        })}
                    </SetupModeCardGroup>

                    {setupMode === 'template' && (
                        <TemplateSection data-testid={`${CREATE_DEAL_IDENTIFIER}-template-section`}>
                            <Typography variant="strong-md" tag="div">
                                Select a Template
                            </Typography>
                            <ManualTemplateList
                                selectionOnly
                                selectedTemplateId={selectedTemplateId}
                                onTemplateSelect={handleTemplateSelect}
                                vehicleCriteria={selectedVehicle}
                            />
                        </TemplateSection>
                    )}
                </Box>
            </SlideOutWithFixedFooter>
            {isApplying && setupMode === 'template' && <FirstPencilCreatingOverlay />}
        </ThemeProvider>
    );
};

export default CreateDealSlideOutContent;
